import { useState, useEffect } from 'react';
import { db } from '../config/firebase';
import { collection, query, orderBy, getDocs, limit, startAfter } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';

export default function HistoryDetailModal({ historyItem, onClose }) {
  const { user } = useAuth();
  const [chatMessages, setChatMessages] = useState([]);
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);
  const [notes, setNotes] = useState([]);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const MESSAGES_PER_PAGE = 50;

  useEffect(() => {
    // Load chat history if chatSaved is true
    if (historyItem?.chatSaved && historyItem?.meetingId) {
      loadChatHistory();
    }

    // Load notes if meeting has notes
    if (historyItem?.meetingId) {
      loadNotes();
    }
  }, [historyItem]);

  const loadChatHistory = async (loadMore = false) => {
    setIsLoadingChat(true);
    try {
      const chatRef = collection(db, 'meetings', historyItem.meetingId, 'chatHistory');
      let q;

      if (loadMore && lastDoc) {
        q = query(
          chatRef,
          orderBy('timestamp', 'asc'),
          startAfter(lastDoc),
          limit(MESSAGES_PER_PAGE)
        );
      } else {
        q = query(chatRef, orderBy('timestamp', 'asc'), limit(MESSAGES_PER_PAGE));
      }

      const snapshot = await getDocs(q);
      const messages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      if (loadMore) {
        setChatMessages(prev => [...prev, ...messages]);
      } else {
        setChatMessages(messages);
      }

      setHasMoreMessages(snapshot.docs.length === MESSAGES_PER_PAGE);
      setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
    } catch (error) {
      console.error('Error loading chat history:', error);
    } finally {
      setIsLoadingChat(false);
    }
  };

  const loadMoreMessages = () => {
    loadChatHistory(true);
  };

  const loadNotes = async () => {
    setIsLoadingNotes(true);
    try {
      const notesRef = collection(db, 'meetings', historyItem.meetingId, 'notes');
      const q = query(notesRef, orderBy('timestamp', 'asc'));
      const snapshot = await getDocs(q);

      const meetingNotes = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(note => {
          // Show shared notes OR private notes that belong to current user
          return !note.isPrivate || note.userId === user?.uid;
        });

      setNotes(meetingNotes);
    } catch (error) {
      console.error('Error loading notes:', error);
    } finally {
      setIsLoadingNotes(false);
    }
  };

  if (!historyItem) return null;

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return 'N/A';

    // Convert seconds to hours, minutes, seconds
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    // Format based on duration
    if (hours > 0) {
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    } else if (minutes > 0) {
      return secs > 0 ? `${minutes}m ${secs}s` : `${minutes}m`;
    } else {
      return `${secs}s`;
    }
  };

  const getInitial = (name) => {
    return name ? name.charAt(0).toUpperCase() : 'U';
  };

  const formatNoteDuration = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const getGradient = (index) => {
    const gradients = [
      'from-blue-500 to-purple-500',
      'from-green-500 to-teal-500',
      'from-pink-500 to-rose-500',
      'from-orange-500 to-red-500',
      'from-indigo-500 to-blue-500',
      'from-yellow-500 to-orange-500',
      'from-purple-500 to-pink-500',
      'from-teal-500 to-green-500',
    ];
    return gradients[index % gradients.length];
  };

  const downloadRecording = () => {
    if (historyItem?.recordingUrl) {
      window.open(historyItem.recordingUrl, '_blank');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
      <div
        className="bg-white dark:bg-[#1c1c1e] rounded-3xl shadow-2xl w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col border border-[#d2d2d7] dark:border-[#2c2c2e] transition-colors"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-[#f5f5f7] dark:bg-[#2c2c2e] border-b border-[#d2d2d7] dark:border-[#3c3c3e] px-6 py-4 flex justify-between items-center flex-shrink-0 transition-colors">
          <div>
            <h2 className="text-xl font-semibold text-[#1d1d1f] dark:text-white transition-colors">
              Meeting Details
            </h2>
            <p className="text-sm text-[#86868b] dark:text-[#8e8e93] transition-colors">
              {historyItem.type === 'meeting' || historyItem.type === 'video' ? 'Video Call' : 'Chat Room'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[#e8e8ed] dark:hover:bg-[#3c3c3e] rounded-lg transition-colors"
            title="Close"
          >
            <svg className="w-6 h-6 text-[#86868b] dark:text-[#8e8e93]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
          {/* Meeting Info Section */}
          <div>
            <h3 className="text-lg font-semibold text-[#1d1d1f] dark:text-white mb-4 transition-colors">
              Meeting Information
            </h3>
            <div className="bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-2xl p-4 space-y-3 transition-colors">
              <div className="flex justify-between">
                <span className="text-sm text-[#86868b] dark:text-[#8e8e93] transition-colors">Meeting ID</span>
                <span className="text-sm font-mono font-semibold text-[#1d1d1f] dark:text-white transition-colors">
                  {historyItem.meetingId || historyItem.id}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-[#86868b] dark:text-[#8e8e93] transition-colors">Start Time</span>
                <span className="text-sm font-medium text-[#1d1d1f] dark:text-white transition-colors">
                  {historyItem.startTime
                    ? formatDate(historyItem.startTime)
                    : formatDate(historyItem.createdAt || historyItem.timestamp)}
                </span>
              </div>
              {historyItem.endTime && (
                <div className="flex justify-between">
                  <span className="text-sm text-[#86868b] dark:text-[#8e8e93] transition-colors">End Time</span>
                  <span className="text-sm font-medium text-[#1d1d1f] dark:text-white transition-colors">
                    {formatDate(historyItem.endTime)}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-sm text-[#86868b] dark:text-[#8e8e93] transition-colors">Duration</span>
                <span className="text-sm font-medium text-[#1d1d1f] dark:text-white transition-colors">
                  {formatDuration(historyItem.duration)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-[#86868b] dark:text-[#8e8e93] transition-colors">Type</span>
                <span className="text-sm font-medium text-[#1d1d1f] dark:text-white transition-colors">
                  {historyItem.type === 'meeting' || historyItem.type === 'video' ? 'Video Call' : 'Chat Room'}
                </span>
              </div>
            </div>
          </div>

          {/* Participants Section */}
          {historyItem.participants && historyItem.participants.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-[#1d1d1f] dark:text-white mb-4 transition-colors">
                Participants ({historyItem.participants.length})
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {historyItem.participants.map((participant, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-xl p-3 transition-colors"
                  >
                    <div
                      className={`w-10 h-10 rounded-full bg-gradient-to-r ${getGradient(index)} flex items-center justify-center text-white font-semibold flex-shrink-0`}
                    >
                      {getInitial(participant.name || participant)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-[#1d1d1f] dark:text-white truncate transition-colors">
                        {participant.name || participant}
                      </p>
                      {participant.email && (
                        <p className="text-xs text-[#86868b] dark:text-[#8e8e93] truncate transition-colors">
                          {participant.email}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recording Section */}
          {historyItem.recordingId && (
            <div>
              <h3 className="text-lg font-semibold text-[#1d1d1f] dark:text-white mb-4 transition-colors">
                Recording
              </h3>
              <div className="bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-2xl p-4 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[#1d1d1f] dark:text-white transition-colors">
                        Recording Available
                      </p>
                      <p className="text-xs text-[#86868b] dark:text-[#8e8e93] transition-colors">
                        Click to download or view
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={downloadRecording}
                    className="px-4 py-2 bg-gradient-to-r from-[#06c] to-[#0055d4] text-white rounded-lg font-medium hover:shadow-lg hover:scale-105 transition-all text-sm"
                  >
                    Download
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Chat History Section */}
          {historyItem.chatSaved && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#1d1d1f] dark:text-white transition-colors">
                  Chat History
                </h3>
                <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium rounded-full transition-colors">
                  Saved
                </span>
              </div>

              {isLoadingChat && chatMessages.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="w-12 h-12 border-4 border-[#06c] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-sm text-[#86868b] dark:text-[#8e8e93] transition-colors">
                      Loading chat history...
                    </p>
                  </div>
                </div>
              ) : chatMessages.length === 0 ? (
                <div className="bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-2xl p-8 text-center transition-colors">
                  <div className="w-16 h-16 bg-[#d2d2d7] dark:bg-[#3c3c3e] rounded-full flex items-center justify-center mx-auto mb-4 transition-colors">
                    <svg className="w-8 h-8 text-[#86868b] dark:text-[#8e8e93]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <p className="text-[#86868b] dark:text-[#8e8e93] transition-colors">
                    No messages in this meeting
                  </p>
                </div>
              ) : (
                <div className="bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-2xl p-4 space-y-3 max-h-96 overflow-y-auto custom-scrollbar transition-colors">
                  {chatMessages.map((msg, index) => (
                    <div key={msg.id || index} className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-[#1d1d1f] dark:text-white transition-colors">
                          {msg.sender || msg.userName || 'Unknown'}
                        </span>
                        <span className="text-[10px] text-[#86868b] dark:text-[#8e8e93] transition-colors">
                          {msg.timestamp
                            ? new Date(msg.timestamp.toDate ? msg.timestamp.toDate() : msg.timestamp).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            : ''}
                        </span>
                      </div>
                      <div className="bg-white dark:bg-[#1c1c1e] rounded-lg px-3 py-2 transition-colors">
                        <p className="text-sm text-[#1d1d1f] dark:text-white leading-relaxed break-words transition-colors">
                          {msg.message || msg.text}
                        </p>
                      </div>
                    </div>
                  ))}

                  {hasMoreMessages && (
                    <button
                      onClick={loadMoreMessages}
                      disabled={isLoadingChat}
                      className="w-full py-2.5 bg-white dark:bg-[#1c1c1e] text-[#06c] rounded-lg font-medium hover:bg-[#e8e8ed] dark:hover:bg-[#3c3c3e] transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isLoadingChat ? (
                        <>
                          <div className="w-4 h-4 border-2 border-[#06c] border-t-transparent rounded-full animate-spin"></div>
                          Loading...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                          Load More ({MESSAGES_PER_PAGE} at a time)
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Meeting Notes Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#1d1d1f] dark:text-white transition-colors">
                Meeting Notes
              </h3>
              {notes.length > 0 && (
                <span className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 text-xs font-medium rounded-full transition-colors">
                  {notes.length} {notes.length === 1 ? 'note' : 'notes'}
                </span>
              )}
            </div>

            {isLoadingNotes ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="w-12 h-12 border-4 border-[#06c] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-sm text-[#86868b] dark:text-[#8e8e93] transition-colors">
                    Loading notes...
                  </p>
                </div>
              </div>
            ) : notes.length === 0 ? (
              <div className="bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-2xl p-8 text-center transition-colors">
                <div className="w-16 h-16 bg-[#d2d2d7] dark:bg-[#3c3c3e] rounded-full flex items-center justify-center mx-auto mb-4 transition-colors">
                  <svg className="w-8 h-8 text-[#86868b] dark:text-[#8e8e93]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <p className="text-[#86868b] dark:text-[#8e8e93] transition-colors">
                  No notes taken during this meeting
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {notes.map((note) => (
                  <div
                    key={note.id}
                    className="bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-2xl p-4 transition-colors"
                  >
                    {/* Note Header */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 text-xs text-[#86868b] dark:text-[#8e8e93]">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="font-medium">{formatNoteDuration(note.meetingDuration || 0)}</span>
                        <span>•</span>
                        <span>
                          {note.timestamp
                            ? new Date(note.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                            : ''}
                        </span>
                      </div>
                      {note.isPrivate ? (
                        <div className="flex items-center gap-1 px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 border border-purple-300 dark:border-purple-700/50 rounded-full">
                          <svg className="w-3 h-3 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          <span className="text-purple-600 dark:text-purple-400 text-xs font-medium">Private</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 px-2 py-0.5 bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700/50 rounded-full">
                          <svg className="w-3 h-3 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          <span className="text-green-600 dark:text-green-400 text-xs font-medium">Shared</span>
                        </div>
                      )}
                    </div>

                    {/* Note Author */}
                    <div className="text-[#86868b] dark:text-[#8e8e93] text-xs font-medium mb-2 flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 flex items-center justify-center text-white text-[10px] font-bold">
                        {note.userName?.[0]?.toUpperCase() || 'G'}
                      </div>
                      {note.userName || 'Guest'}
                      {note.userId === user?.uid && <span className="text-[#06c]">(You)</span>}
                    </div>

                    {/* Note Content */}
                    <p className="text-[#1d1d1f] dark:text-white text-sm leading-relaxed whitespace-pre-wrap">
                      {note.content}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-[#f5f5f7] dark:bg-[#2c2c2e] border-t border-[#d2d2d7] dark:border-[#3c3c3e] px-6 py-4 flex justify-end gap-3 flex-shrink-0 transition-colors">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-white dark:bg-[#1c1c1e] text-[#1d1d1f] dark:text-white border border-[#d2d2d7] dark:border-[#3c3c3e] rounded-xl font-medium hover:bg-[#e8e8ed] dark:hover:bg-[#3c3c3e] transition-all"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
